import React, { useState } from 'react';
import axios from 'axios';
import './PlantIdentifier.css';

const API_URL = process.env.REACT_APP_API_URL

function PlantIdentifier({ onPlantSelected }) {
  const [identifying, setIdentifying] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setResults(null);
    setError(null);
  };

  const handleIdentify = async () => {
    if (!selectedFile) {
      setError('Please select a photo first');
      return;
    }

    setIdentifying(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('photo', selectedFile);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/plants/identify`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setResults(response.data.predictions);
    } catch (err) {
      console.error('Identification error:', err);
      setError(err.response?.data?.error ||
        err.code === 'ERR_NETWORK' ? 'Unable to reach identification service. Check your connection and try again.' :
        'Failed to identify plant. Please try again.');
    } finally {
      setIdentifying(false);
    }
  };

  const handleSelectPlant = async (plant) => {
    if (onPlantSelected) {
      onPlantSelected({
        commonName: plant.common_name,
        scientificName: plant.scientific_name,
        plantType: plant.category,
        identificationPhoto: selectedFile
      });
    }
    if (selectedFile) {
      (async () => {
        try {
          const token = localStorage.getItem('token');
          const trainingForm = new FormData();
          trainingForm.append('photo', selectedFile);
          trainingForm.append('scientificName', plant.scientific_name);
          await axios.post(`${API_URL}/plants/identify/confirm`, trainingForm, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (err) {
          console.error('Training log failed (non-critical):', err.message);
        }
      })();
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setResults(null);
    setError(null);
  };

  const confidenceClass = (confidence) => {
    if (confidence > 0.5) return 'plant-id-confidence--high';
    if (confidence > 0.2) return 'plant-id-confidence--medium';
    return 'plant-id-confidence--low';
  };

  return (
    <div className="plant-id-overlay">
      <div className="plant-id-dialog">
        <div className="plant-id-header">
          <h2>📸 Identify Plant</h2>
          <button
            className="plant-id-close-btn"
            onClick={onPlantSelected ? () => onPlantSelected(null) : null}
          >
            ×
          </button>
        </div>

        {!results && (
          <div className="plant-id-upload-wrap">
            <label className="plant-id-dropzone">
              {previewUrl ? (
                <div>
                  <img src={previewUrl} alt="Preview" className="plant-id-preview-img" />
                  <p className="plant-id-preview-ok">✓ Photo selected. Click "Identify" below.</p>
                </div>
              ) : (
                <div>
                  <div className="plant-id-icon">📷</div>
                  <p className="plant-id-prompt">Click to take photo or upload image</p>
                  <p className="plant-id-hint">Supports JPG, PNG</p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="pi-camera"
              />
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="pi-gallery"
              />
            </label>
            <div className="plant-id-btn-group">
              <label htmlFor="pi-camera" className="plant-id-btn-camera">📷 Take Photo</label>
              <label htmlFor="pi-gallery" className="plant-id-btn-gallery">🖼️ Choose Photo</label>
            </div>

            {previewUrl && (
              <button className="plant-id-btn-retry" onClick={handleReset}>
                Choose Different Photo
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="plant-id-error">{error}</div>
        )}

        {!results && selectedFile && (
          <button
            className="plant-id-identify-btn"
            onClick={handleIdentify}
            disabled={identifying}
          >
            {identifying ? '🔍 Identifying...' : '🔍 Identify Plant'}
          </button>
        )}

        {results && (
          <div>
            {previewUrl && (
              <img src={previewUrl} alt="Identified" className="plant-id-results-img" />
            )}

            <h3>Possible Matches:</h3>

            {results.map((plant, idx) => (
              <div
                key={idx}
                className="plant-id-result-card"
                onClick={() => handleSelectPlant(plant)}
              >
                <div className="plant-id-result-body">
                  <div className="plant-id-result-info">
                    <div className="plant-id-result-name">{plant.common_name}</div>
                    <div className="plant-id-result-sci">{plant.scientific_name}</div>
                    <div className="plant-id-result-cat">{plant.category}</div>
                  </div>
                  <div className={`plant-id-confidence ${confidenceClass(plant.confidence)}`}>
                    {Math.round(plant.confidence * 100)}%
                  </div>
                </div>

                {plant.care_info && (
                  <div className="plant-id-care-info">
                    {plant.care_info.care_sun_requirement && (
                      <span className="plant-id-care-span">
                        ☀️ {plant.care_info.care_sun_requirement}
                      </span>
                    )}
                    {plant.care_info.care_water_frequency && (
                      <span>
                        💧 Every {plant.care_info.care_water_frequency} days
                      </span>
                    )}
                  </div>
                )}

                <div className="plant-id-select-hint">→ Tap to add this plant</div>
              </div>
            ))}

            <button
              className="plant-id-btn-none"
              onClick={() => { if (onPlantSelected) onPlantSelected(null); }}
            >
              ✗ None of these are correct
            </button>
            <button className="plant-id-btn-retry-photo" onClick={handleReset}>
              Try Another Photo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlantIdentifier;
